import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close,
  Receipt,
  AttachMoney,
  DateRange,
  Category,
  CreditCard,
  CheckCircle,
} from '@mui/icons-material';
import { LoyaltyProgram, Transaction } from '../../types';

interface AddTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onAddTransaction: (transaction: Omit<Transaction, 'id'>) => void;
  userCards: LoyaltyProgram[];
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    maxWidth: 600,
    width: '100%',
    margin: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const steps = ['Transaction Details', 'Points & Category', 'Review'];

const transactionTypes = [
  { value: 'earned', label: 'Points Earned', color: '#10b981' },
  { value: 'redeemed', label: 'Points Redeemed', color: '#f59e0b' },
  { value: 'expired', label: 'Points Expired', color: '#ef4444' },
  { value: 'transferred', label: 'Points Transferred', color: '#6366f1' },
];

const spendingCategories = [
  { value: 'dining', label: 'Dining & Restaurants', icon: '🍽️' },
  { value: 'travel', label: 'Travel & Transportation', icon: '✈️' },
  { value: 'groceries', label: 'Groceries', icon: '🛒' },
  { value: 'gas', label: 'Gas Stations', icon: '⛽' },
  { value: 'shopping', label: 'Online Shopping', icon: '🛍️' },
  { value: 'entertainment', label: 'Entertainment', icon: '🎬' },
  { value: 'bills', label: 'Bills & Utilities', icon: '💡' },
  { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const AddTransactionDialog: React.FC<AddTransactionDialogProps> = ({ 
  open, 
  onClose, 
  onAddTransaction, 
  userCards 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [transactionData, setTransactionData] = useState({
    programId: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    points: '',
    type: 'earned' as const,
    source: '',
    category: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setTransactionData({
      programId: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      points: '',
      type: 'earned',
      source: '',
      category: '',
    });
    setErrors({});
    onClose();
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    
    if (activeStep === 0) {
      if (!transactionData.programId) newErrors.programId = 'Please select a card/program';
      if (!transactionData.description.trim()) newErrors.description = 'Description is required';
      if (!transactionData.amount.trim()) newErrors.amount = 'Amount is required';
      if (!transactionData.date) newErrors.date = 'Date is required';
    }
    
    if (activeStep === 1) {
      if (!transactionData.points.trim()) newErrors.points = 'Points earned/spent is required';
      if (transactionData.type === 'earned' && !transactionData.category) {
        newErrors.category = 'Category is required for earning transactions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setTransactionData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = () => {
    if (validateStep()) {
      const selectedCard = userCards.find(card => card.id === transactionData.programId);
      
      const newTransaction: Omit<Transaction, 'id'> = {
        programId: transactionData.programId,
        date: new Date(transactionData.date),
        description: transactionData.description,
        points: parseInt(transactionData.points),
        type: transactionData.type,
        source: transactionData.source || `${transactionData.category || 'Manual'} Purchase`,
        balance: selectedCard ? selectedCard.points + parseInt(transactionData.points) : parseInt(transactionData.points),
      };
      
      onAddTransaction(newTransaction);
      handleClose();
    }
  };

  const getSelectedCard = () => {
    return userCards.find(card => card.id === transactionData.programId);
  };

  const calculatePointsFromAmount = () => {
    const amount = parseFloat(transactionData.amount);
    if (!amount || !transactionData.category) return '';
    
    // Basic earning rate calculation (can be enhanced with real card earning structures)
    const earningRates: Record<string, number> = {
      dining: 3,
      travel: 3,
      groceries: 2,
      gas: 2,
      shopping: 1,
      entertainment: 1,
      bills: 1,
      healthcare: 1,
      other: 1,
    };
    
    const rate = earningRates[transactionData.category] || 1;
    return Math.round(amount * rate).toString();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Transaction Details
            </Typography>

            <FormControl fullWidth error={!!errors.programId} sx={{ mb: 3 }}>
              <InputLabel>Select Card/Program</InputLabel>
              <Select
                value={transactionData.programId}
                label="Select Card/Program"
                onChange={(e) => handleInputChange('programId', e.target.value)}
              >
                {userCards.map((card) => (
                  <MenuItem key={card.id} value={card.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCard sx={{ fontSize: 20, color: card.color }} />
                      {card.name}
                      <Chip 
                        label={`${card.points.toLocaleString()} pts`} 
                        size="small" 
                        variant="outlined"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.programId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                  {errors.programId}
                </Typography>
              )}
            </FormControl>

            <StyledTextField
              fullWidth
              label="Transaction Description"
              value={transactionData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={!!errors.description}
              helperText={errors.description || 'e.g., Dinner at Restaurant XYZ'}
              sx={{ mb: 3 }}
              placeholder="What did you purchase?"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <StyledTextField
                label="Amount Spent"
                type="number"
                value={transactionData.amount}
                onChange={(e) => {
                  handleInputChange('amount', e.target.value);
                  // Auto-calculate points if category is selected
                  if (transactionData.category) {
                    const amount = parseFloat(e.target.value);
                    if (amount) {
                      const estimatedPoints = calculatePointsFromAmount();
                      if (estimatedPoints) {
                        handleInputChange('points', estimatedPoints);
                      }
                    }
                  }
                }}
                error={!!errors.amount}
                helperText={errors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ flex: 1 }}
              />

              <StyledTextField
                label="Transaction Date"
                type="date"
                value={transactionData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                error={!!errors.date}
                helperText={errors.date}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={transactionData.type}
                label="Transaction Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                {transactionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: type.color,
                          borderRadius: '50%',
                        }}
                      />
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Points & Category
            </Typography>

            <StyledTextField
              fullWidth
              label={transactionData.type === 'earned' ? 'Points Earned' : 'Points Used'}
              type="number"
              value={transactionData.points}
              onChange={(e) => handleInputChange('points', e.target.value)}
              error={!!errors.points}
              helperText={errors.points || `How many points were ${transactionData.type}?`}
              sx={{ mb: 3 }}
              inputProps={{ min: 0 }}
            />

            {transactionData.type === 'earned' && (
              <FormControl fullWidth error={!!errors.category} sx={{ mb: 3 }}>
                <InputLabel>Spending Category</InputLabel>
                <Select
                  value={transactionData.category}
                  label="Spending Category"
                  onChange={(e) => {
                    handleInputChange('category', e.target.value);
                    // Auto-calculate points when category changes
                    if (transactionData.amount) {
                      const estimatedPoints = calculatePointsFromAmount();
                      if (estimatedPoints) {
                        handleInputChange('points', estimatedPoints);
                      }
                    }
                  }}
                >
                  {spendingCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{category.icon}</span>
                        {category.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.category}
                  </Typography>
                )}
              </FormControl>
            )}

            <StyledTextField
              fullWidth
              label="Source (Optional)"
              value={transactionData.source}
              onChange={(e) => handleInputChange('source', e.target.value)}
              helperText="e.g., Credit Card Spending, Sign-up Bonus, etc."
              placeholder="How were these points earned/spent?"
            />

            {transactionData.amount && transactionData.category && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💡 Estimated points: {calculatePointsFromAmount()} points 
                  (₹{transactionData.amount} × {spendingCategories.find(c => c.value === transactionData.category)?.label} rate)
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 2:
        const selectedCard = getSelectedCard();
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Review Transaction
            </Typography>

            <Box sx={{ 
              p: 3, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2, 
              mb: 3 
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Card/Program:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedCard?.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Description:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {transactionData.description}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Amount:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  ₹{transactionData.amount}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Points:</Typography>
                <Typography variant="body2" fontWeight={600} color={
                  transactionData.type === 'earned' ? 'success.main' : 'warning.main'
                }>
                  {transactionData.type === 'earned' ? '+' : '-'}{transactionData.points} points
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Date:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {new Date(transactionData.date).toLocaleDateString()}
                </Typography>
              </Box>
              
              {transactionData.category && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Category:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {spendingCategories.find(c => c.value === transactionData.category)?.label}
                  </Typography>
                </Box>
              )}
            </Box>

            <Alert severity="success">
              <Typography variant="body2">
                Transaction will be added to your {selectedCard?.name} history and used for spending analysis.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Add Transaction
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
        )}
        
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<CheckCircle />}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              fontWeight: 600,
            }}
          >
            Add Transaction
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            startIcon={<Receipt />}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
              fontWeight: 600,
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default AddTransactionDialog;